/**
 * Variant Cache Section - Cache settings (Enable, TTL) for text, gateway, path, lander, offer.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Save, Database } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { Switch } from '@/components/ui';
import {
  useTagRulesSettings,
  useUpdateTagRulesSettings,
  type TagRuleVariant,
} from '@/hooks';
import { useToast } from '@/providers/ToastProvider';
import { getApiErrorMessage } from '@/lib/utils';
import { cn } from '@/lib/utils';

const CACHE_TTL_MIN = 1;
const CACHE_TTL_MAX = 168;

const VARIANT_LABELS: Record<TagRuleVariant, string> = {
  text: 'Text tag cache',
  gateway: 'Gateway tag cache',
  path: 'Path tag cache',
  lander: 'Lander tag cache',
  offer: 'Offer tag cache',
  phone: 'Phone tag cache',
  drop: 'Drop tag cache',
  source: 'Source tag cache',
  hour: 'Hour tag cache',
};

interface VariantCacheSectionProps {
  variant: TagRuleVariant;
}

export const VariantCacheSection: FC<VariantCacheSectionProps> = ({ variant }) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const { data: settings, isLoading } = useTagRulesSettings(variant);
  const updateSettings = useUpdateTagRulesSettings(variant);
  const [cacheTtlHours, setCacheTtlHours] = useState<string>('12');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings?.cacheTtlHours != null)
      setCacheTtlHours(String(settings.cacheTtlHours));
  }, [settings?.cacheTtlHours]);

  const parsedTtl = Math.max(
    CACHE_TTL_MIN,
    Math.min(CACHE_TTL_MAX, parseInt(cacheTtlHours, 10) || 12),
  );
  const ttlValid =
    /^\d+$/.test(cacheTtlHours.trim()) &&
    parsedTtl >= CACHE_TTL_MIN &&
    parsedTtl <= CACHE_TTL_MAX;

  const hasAnyChange = useCallback(() => {
    return ttlValid && parsedTtl !== settings?.cacheTtlHours;
  }, [ttlValid, parsedTtl, settings?.cacheTtlHours]);

  useEffect(() => {
    setHasChanges(hasAnyChange());
  }, [hasAnyChange]);

  const handleSave = async () => {
    if (!ttlValid || parsedTtl === settings?.cacheTtlHours) return;
    try {
      await updateSettings.mutateAsync({ cacheTtlHours: parsedTtl });
      setHasChanges(false);
      toastSuccess('Settings saved', `${VARIANT_LABELS[variant]} TTL updated.`);
    } catch (e) {
      toastError('Save failed', getApiErrorMessage(e, 'Could not save settings'));
    }
  };

  const handleTtlBlur = () => {
    if (ttlValid && parsedTtl !== settings?.cacheTtlHours) setHasChanges(true);
  };

  const handleTtlChange = (value: string) => setCacheTtlHours(value);

  const handleCacheEnabledChange = async (enabled: boolean) => {
    try {
      await updateSettings.mutateAsync({ cacheEnabled: enabled });
      toastSuccess(
        'Cache updated',
        enabled
          ? `${VARIANT_LABELS[variant]} enabled.`
          : `${VARIANT_LABELS[variant]} disabled.`,
      );
    } catch (e) {
      toastError('Update failed', getApiErrorMessage(e, 'Could not update cache'));
    }
  };

  const label = VARIANT_LABELS[variant];

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border/50 bg-muted/20 px-6 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground">{label}</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Cache performance tags to reduce Voluum API calls. Invalidated on
              rule or settings change.
            </p>
          </div>
          {hasChanges && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSave}
              disabled={updateSettings.isPending}
              className="shrink-0"
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              Save changes
            </Button>
          )}
        </div>
      </div>
      <div className="px-6 py-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Switch
              id={`cache-enabled-${variant}`}
              checked={settings?.cacheEnabled ?? true}
              onCheckedChange={handleCacheEnabledChange}
              disabled={isLoading || updateSettings.isPending}
            />
            <label
              htmlFor={`cache-enabled-${variant}`}
              className="text-sm font-medium"
            >
              Enable cache
            </label>
          </div>
          <div
            className={cn(
              'flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2',
              isLoading && 'pointer-events-none opacity-60',
            )}
          >
            <label className="text-xs font-medium text-muted-foreground sm:sr-only">
              TTL
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={CACHE_TTL_MIN}
                max={CACHE_TTL_MAX}
                value={cacheTtlHours}
                onChange={(e) => handleTtlChange(e.target.value)}
                onBlur={handleTtlBlur}
                disabled={isLoading}
                className="h-9 w-20 text-center"
                aria-label="Cache TTL hours"
              />
              <span className="text-sm text-muted-foreground">hours</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
